import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController, type ValidationConstraints } from '../utils/form-validation.controller.js';
import styles from './styles.js';

export type TextareaVariant      = 'outlined' | 'filled';
export type TextareaSize         = 'small' | 'medium' | 'large';
export type TextareaResize       = 'both' | 'horizontal' | 'vertical' | 'none';
export type TextareaFloatVariant = 'over' | 'in' | 'on';

/**
 * uwc-textarea
 *
 * Multi-line text input with W3C form association, constraint validation,
 * theming, float labels, auto-resize, and char count.
 *
 * @attr value        - Current textarea value.
 * @attr placeholder  - Placeholder text. Ignored when `label` is set, since
 *                      the floating label rests inside the field as the placeholder.
 * @attr rows         - Visible row count. Default: 4.
 * @attr cols         - Column hint (usually overridden by CSS width).
 * @attr resize       - both | horizontal | vertical | none. Default: vertical.
 * @attr auto-resize  - Grow height automatically to fit content.
 * @attr variant      - outlined | filled. Default: outlined.
 * @attr size         - small | medium | large. Default: medium.
 * @attr disabled     - Disable the textarea.
 * @attr readonly     - Make the textarea read-only.
 * @attr invalid      - Mark as invalid (red border).
 * @attr fluid        - Stretch to fill container width.
 * @attr required     - Mark the field as required.
 * @attr minlength    - Minimum character count.
 * @attr maxlength    - Maximum character count.
 * @attr pattern      - Regex pattern for validation.
 * @attr show-count   - Show character counter (requires maxlength).
 * @attr name         - Name for form submission.
 * @attr autocomplete - Native autocomplete attribute.
 * @attr label        - Floating label text. Activates float-label mode when set.
 * @attr float-label  - Float label variant: over (default) | in | on.
 *
 * @fires uwc-input   - Fired on every keystroke. Detail: { value }.
 * @fires uwc-change  - Fired when value is committed (blur). Detail: { value }.
 * @fires uwc-focus   - Fired on focus.
 * @fires uwc-blur    - Fired on blur.
 * @fires uwc-invalid - Fired when field is blurred with an invalid value.
 *                      Detail: { validity, validationMessage }
 */
export class UwcTextarea extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Core ─────────────────────────────────────────────────────────

  @property()               value        = '';
  @property()               placeholder?: string;
  @property({ type: Number }) rows        = 4;
  @property({ type: Number }) cols?:      number;
  @property()               name?:       string;
  @property()               autocomplete?: string;

  // ── Validation constraints ────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) required  = false;
  @property({ type: Number }) minlength?: number;
  @property({ type: Number }) maxlength?: number;
  @property() pattern?: string;

  // ── Appearance ───────────────────────────────────────────────────

  @property({ reflect: true }) variant:    TextareaVariant  = 'outlined';
  @property({ reflect: true }) size:       TextareaSize     = 'medium';
  @property({ reflect: true }) resize:     TextareaResize   = 'vertical';
  @property()                  label?:     string;
  @property({ attribute: 'float-label', reflect: true })
  floatLabel: TextareaFloatVariant = 'over';

  // ── State modifiers ──────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) disabled   = false;
  @property({ type: Boolean, reflect: true }) readonly   = false;
  @property({ type: Boolean, reflect: true }) invalid    = false;
  @property({ type: Boolean, reflect: true }) fluid      = false;
  @property({ type: Boolean, reflect: true, attribute: 'auto-resize' })
  autoResize = false;
  @property({ type: Boolean, attribute: 'show-count' })
  showCount = false;

  // ── Internal ─────────────────────────────────────────────────────

  @state() private _charCount = 0;

  private get _constraints(): ValidationConstraints {
    return {
      required:  this.required,
      minlength: this.minlength,
      maxlength: this.maxlength,
      pattern:   this.pattern,
    };
  }

  override firstUpdated() {
    this._charCount = this.value?.length ?? 0;
    if (this.autoResize) this._doAutoResize();
    const ta = this.shadowRoot?.querySelector('textarea') ?? null;
    this._validation.setAnchor(ta);
    this._validation.syncFormValue(this.value || null);
    this._validation.revalidate(this.value, this._constraints);
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    const constraintKeys: PropertyKey[] = ['required', 'minlength', 'maxlength', 'pattern'];
    if (constraintKeys.some(k => changed.has(k))) {
      this._validation.revalidate(this.value, this._constraints);
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────

  private _onInput(e: Event) {
    const ta = e.target as HTMLTextAreaElement;
    this.value = ta.value;
    this._charCount = ta.value.length;
    if (this.autoResize) this._doAutoResize();
    this._validation.handleChange(this.value, this._constraints);
    this._validation.syncFormValue(this.value || null);
    this.dispatchEvent(new CustomEvent('uwc-input', {
      bubbles: true, composed: true,
      detail: { value: this.value, originalEvent: e },
    }));
  }

  private _onChange(e: Event) {
    const ta = e.target as HTMLTextAreaElement;
    this.value = ta.value;
    this._validation.syncFormValue(this.value || null);
    this.dispatchEvent(new CustomEvent('uwc-change', {
      bubbles: true, composed: true,
      detail: { value: this.value, originalEvent: e },
    }));
  }

  private _onBlur() {
    this._validation.setTouched(true);
    this.dispatchEvent(new CustomEvent('uwc-blur', { bubbles: true, composed: true }));
  }

  private _doAutoResize() {
    const ta = this.shadowRoot?.querySelector('textarea');
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }

  // ── Render ────────────────────────────────────────────────────────

  override render() {
    const floatMode = !!this.label;
    const classes = classMap({
      'uwc-textarea': true,
      [`uwc-textarea--${this.variant}`]: true,
      [`uwc-textarea--${this.size}`]:    this.size !== 'medium',
      'uwc-textarea--invalid':  this.invalid,
      'uwc-textarea--disabled': this.disabled,
      'uwc-textarea--float':    floatMode,
      [`uwc-textarea--float-${this.floatLabel}`]: floatMode,
    });

    // In float-label mode the label rests inside the field and doubles as the
    // placeholder, so any user-supplied placeholder is ignored to avoid it
    // overlapping the resting label. A single space is used instead so
    // :placeholder-shown still drives the CSS float transition.
    const placeholder = floatMode ? ' ' : this.placeholder;

    const showFooter = this.showCount && this.maxlength !== undefined;
    const nearLimit  = this.maxlength !== undefined && this._charCount >= this.maxlength * 0.9;

    return html`
      <div part="wrapper" class=${classes}>
        <textarea
          part="textarea"
          .value=${this.value}
          rows=${this.rows}
          cols=${ifDefined(this.cols)}
          placeholder=${ifDefined(placeholder)}
          name=${ifDefined(this.name)}
          autocomplete=${ifDefined(this.autocomplete)}
          maxlength=${ifDefined(this.maxlength)}
          minlength=${ifDefined(this.minlength)}
          pattern=${ifDefined(this.pattern)}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          ?required=${this.required}
          aria-required=${this.required ? 'true' : nothing}
          aria-multiline="true"
          @input=${this._onInput}
          @change=${this._onChange}
          @focus=${() => this.dispatchEvent(new CustomEvent('uwc-focus', { bubbles: true, composed: true }))}
          @blur=${this._onBlur}
        ></textarea>
        ${floatMode
          ? html`<label class="uwc-textarea__label" part="label">${this.label}</label>`
          : nothing}
        ${showFooter ? html`
          <div part="footer" class="uwc-textarea__footer">
            <span part="count" class="uwc-textarea__count${nearLimit ? ' uwc-textarea__count--warn' : ''}">
              ${this._charCount} / ${this.maxlength}
            </span>
          </div>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-textarea': UwcTextarea; }
}
